import { useMemo } from 'react'

import { useQuery } from '@apollo/client/react'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

export const UP_NEXT_POSTS_LIMIT = 3

export const UpNextPost_Post = graphql(`
  fragment UpNextPost_Post on Post {
    id
    text
    dueAt
    channel {
      id
      service
      name
      avatar
    }
    assets {
      __typename
      thumbnail
      source
    }
  }
`)

export type UpNextPost = ResultOf<typeof UpNextPost_Post>

export const GetUpNextPosts = graphql(
  `
    query GetUpNextPosts(
      $organizationId: OrganizationId!
      $first: Int!
      $sort: [PostSortInput!]
    ) {
      posts(
        input: {
          organizationId: $organizationId
          filter: { status: [scheduled] }
          sort: $sort
        }
        first: $first
      ) {
        edges {
          node {
            ...UpNextPost_Post
          }
        }
      }
      postCounts(
        input: {
          organizationId: $organizationId
          filter: { status: [scheduled] }
        }
      ) {
        counts {
          channelId
          count
        }
      }
    }
  `,
  [UpNextPost_Post],
)

type UseUpNextPostsResult = {
  posts: UpNextPost[]
  totalCount: number
  loading: boolean
  error: Error | undefined
  refetch: () => Promise<unknown>
}

export const useUpNextPosts = (
  limit: number = UP_NEXT_POSTS_LIMIT,
): UseUpNextPostsResult => {
  const organizationId = useOrganizationId()

  const { data, loading, error, refetch } = useQuery(GetUpNextPosts, {
    variables: {
      organizationId: organizationId ?? '',
      first: limit,
      sort: [
        { field: 'dueAt', direction: 'asc' },
        { field: 'createdAt', direction: 'desc' },
      ],
    },
    skip: !organizationId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  })

  return useMemo(
    () => ({
      posts:
        data?.posts?.edges?.map((edge) =>
          readFragment(UpNextPost_Post, edge.node),
        ) ?? [],
      totalCount:
        data?.postCounts?.counts?.reduce((sum, { count }) => sum + count, 0) ??
        0,
      loading,
      error,
      refetch,
    }),
    [data, loading, error, refetch],
  )
}
